import axios from 'axios';

// Temporary constant until we create the constants file
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Types
interface Vendor {
  _id: string;
  name: string;
  type: string;
}

interface Employee {
  _id: string;
  name: string;
  role: string;
}

interface Project {
  _id: string;
  name: string;
  mission?: string;
}

interface ExpenseData {
  amount: number;
  description: string;
  project: string;
  projectMission: string;
  charitablePurpose: string;
  dateSubmitted: string;
  submittedBy: string;
  approvalPath: string;
  attachments: Array<{
    name: string;
    size: number;
  }>;
  submissionToken: string;
}

// Add Transaction type
interface Transaction {
  _id: string;
  date: string;
  type: string;
  description: string;
  amount: number;
  status: string;
  fiscalSponsorId: string;
  metadata?: Record<string, any>;
}

// Fetch Functions
export const fetchVendors = async (): Promise<Vendor[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/vendors`);
    return response.data;
  } catch (error) {
    console.error('Error fetching vendors:', error);
    throw new Error('Failed to fetch vendors');
  }
};

export const fetchEmployees = async (): Promise<Employee[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/employees`);
    return response.data;
  } catch (error) {
    console.error('Error fetching employees:', error);
    throw new Error('Failed to fetch employees');
  }
};

export const fetchProjects = async (): Promise<Project[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/projects`);
    return response.data;
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw new Error('Failed to fetch projects');
  }
};

// Create Request Functions
export const createReimbursementRequest = async (data: any): Promise<any> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/expenses/reimbursement`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating reimbursement request:', error);
    throw new Error('Failed to create reimbursement request');
  }
};

export const createContractorPayment = async (data: any): Promise<any> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/expenses/contractor`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating contractor payment:', error);
    throw new Error('Failed to create contractor payment');
  }
};

export const createExpenseRequest = async (data: any): Promise<any> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/expenses`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating expense request:', error);
    throw new Error('Failed to create expense request');
  }
};

// Existing Functions
export const createExpense = async (expenseData: ExpenseData): Promise<void> => {
  try {
    const formData = new FormData();
    
    // Add expense data
    Object.entries(expenseData).forEach(([key, value]) => {
      if (key !== 'attachments') {
        formData.append(key, value);
      }
    });

    // Add attachments
    expenseData.attachments.forEach((attachment, index) => {
      formData.append(`attachment${index}`, attachment);
    });

    const response = await axios.post(`${API_BASE_URL}/expenses`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create expense');
    }
  } catch (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
};

export const validateExpense = async (expenseData: ExpenseData): Promise<boolean> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/expenses/validate`, expenseData);
    return response.data.valid;
  } catch (error) {
    console.error('Error validating expense:', error);
    throw new Error('Failed to validate expense');
  }
};

export const uploadAttachment = async (file: File): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(`${API_BASE_URL}/attachments/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.fileUrl;
  } catch (error) {
    console.error('Error uploading attachment:', error);
    throw new Error('Failed to upload attachment');
  }
};

export const processOCR = async (imageData: string): Promise<{
  amount?: string;
  description?: string;
  date?: string;
}> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/ocr/process`, { imageData });
    return response.data;
  } catch (error) {
    console.error('Error processing OCR:', error);
    throw new Error('Failed to process OCR');
  }
};

// Add fetchTransactions function
/**
 * @param {string} fiscalSponsorId
 * @returns {Promise<import('./types').Transaction[]>}
 */
export const fetchTransactions = async (fiscalSponsorId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/transactions`, {
      params: { fiscalSponsorId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching transactions:', error);
    throw new Error('Failed to fetch transactions');
  }
};

/**
 * @param {{ transactionId: string, status: string, metadata?: Record<string, any> }} data
 * @returns {Promise<import('./types').Transaction>}
 */
export const updateTransactionStatus = async (data) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/transactions/${data.transactionId}/status`, {
      status: data.status,
      metadata: data.metadata
    });
    return response.data;
  } catch (error) {
    console.error('Error updating transaction status:', error);
    throw new Error('Failed to update transaction status');
  }
};

export default {
  fetchVendors,
  fetchEmployees,
  fetchProjects,
  createReimbursementRequest,
  createContractorPayment,
  createExpenseRequest,
  createExpense,
  validateExpense,
  uploadAttachment,
  processOCR,
  fetchTransactions,
  updateTransactionStatus,
}; 